import { Autoplay, EffectCoverflow, Pagination } from "swiper/modules"
import type { Swiper, SwiperOptions } from "swiper/types"
import "swiper/css/pagination"
import { throttle } from "lodash-es"
// @ts-expect-error typescript cannot find a type for this file.
import swiperCssAutoplay from "swiper/element/css/autoplay?raw"
// @ts-expect-error typescript cannot find a type for this file.
import swiperCssCoverflow from "swiper/element/css/effect-coverflow?raw"
// @ts-expect-error typescript cannot find a type for this file.
import swiperCssNavigation from "swiper/element/css/navigation?raw"
// @ts-expect-error typescript cannot find a type for this file.
import swiperCssPagination from "swiper/element/css/pagination?raw"

const sharedConfig = {
    observer: true,
    observeParents: true,
    loop: true,
    loopAdditionalSlides: 0,
}

// original source: CBanner.php portale-yii
export function getSwiperConfig(
    bannerPlace: string,
    bannerCount: number,
    sliderType: "coverflow" | "slider",
): SwiperOptions {
    if (sliderType === "slider") {
        return {
            ...sharedConfig,
            modules: [Autoplay, Pagination],
            injectStyles: [swiperCssAutoplay, swiperCssPagination],
            speed: 650,
            spaceBetween: 30,
            // TODO bug in swiper lets it jump. has potential fix? https://github.com/nolimits4web/swiper/issues/7270
            // centeredSlides: true,
            pagination: {
                enabled: true,
            },
            effect: "slide",
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            on: {
                slideChange: throttle((swiper) => {
                    updateBannerHeading(swiper.slides[swiper.activeIndex])
                }, 100),
            },
        }
    }
    if (sliderType === "coverflow") {
        return {
            ...sharedConfig,
            modules: [Autoplay, EffectCoverflow],
            injectStyles: [swiperCssAutoplay, swiperCssCoverflow],
            speed: 650,
            threshold: 3,
            watchSlidesProgress: true,
            slidesPerView: "auto",
            effect: "coverflow",
            centeredSlides: true,
            loopAdditionalSlides: 2,
            coverflowEffect: {
                rotate: 0,
                stretch: 100,
                depth: 200,
                modifier: 1,
            },
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            on: {
                afterInit: (swiper) => {
                    fillSwiper(swiper, 12)
                    updateCoverflowTransparency(swiper)
                },
                slideChange: throttle((swiper) => {
                    updateCoverflowTransparency(swiper)
                    updateBannerHeading(swiper.slides[swiper.activeIndex])
                }, 100),
            },
        }
    }

    let lgCount = 5
    let lgContainerWidth = 1140

    const mdCount = 3
    let mdContainerWidth = 672

    const smCount = 2
    let smContainerWidth = 438

    if (bannerPlace === "muenchen-highlight") {
        lgCount = 4

        lgContainerWidth = 1000
        mdContainerWidth = 702
        smContainerWidth = 468
    }

    return {
        ...sharedConfig,
        speed: 900,
        initialSlide: 0,
        spaceBetween: 30,
        navigation: {
            nextEl: `.${!bannerPlace ? `${bannerPlace}-` : ""}right`,
            prevEl: `.${!bannerPlace ? `${bannerPlace}-` : ""}left`,
        },
        width: smContainerWidth,
        slidesPerView: smCount,
        slidesPerGroup: smCount,
        centerInsufficientSlides: bannerCount < smCount,
        // loopFillGroupWithBlank: bannerCount > smCount,todo does not exists?
        breakpoints: {
            990: {
                width: mdContainerWidth,
                slidesPerView: mdCount,
                slidesPerGroup: mdCount,
                centerInsufficientSlides: bannerCount < mdCount,
                // loopFillGroupWithBlank: bannerCount > mdCount, todo does not exist?
                loop: true,
            },
            1200: {
                width: lgContainerWidth,
                slidesPerView: lgCount,
                slidesPerGroup: lgCount,
                centerInsufficientSlides: bannerCount < lgCount,
                // loopFillGroupWithBlank: bannerCount > lgCount, todo does not exist?
                loop: true,
            },
        },
    }
}

function fillSwiper(swiper: Swiper, minFillUntil: number) {
    let count = swiper.slides.length
    const originalCount = count
    // fillUntilDivisible has to be divisible by originalCount as otherwise the order could be different in some areas
    // 121 (3, not divisible by 2)
    // user would see 1, 2, 1, 1
    const fillUntilDivisible = originalCount * Math.ceil(minFillUntil / originalCount)
    while (count < fillUntilDivisible) {
        const swiperSlideIndex = count % originalCount
        // @ts-ignore hostEl exists on swiper, swiper package has old/missing types?
        swiper.hostEl.appendChild(swiper.slides[swiperSlideIndex].cloneNode(true))
        count += 1
    }
}

function updateCoverflowTransparency(swiper: Swiper) {
    const easing = (x: number) => 1 - Math.sqrt(1 - x ** 2)

    const manualSlide = !swiper.autoplay?.running

    for (let slideIndex = 0, slideCount = swiper.slides.length; slideIndex < slideCount; slideIndex++) {
        const slide = swiper.slides[slideIndex]
        const slideImage = slide.querySelector("img")

        if (!(slide instanceof HTMLElement) || !(slideImage instanceof HTMLElement)) {
            continue
        }

        if (manualSlide) {
            slide.classList.remove("slide-css-fade-opacity")
            slideImage.classList.remove("slide-css-fade-opacity")
        } else {
            slide.classList.add("slide-css-fade-opacity")
            slideImage.classList.add("slide-css-fade-opacity")
        }

        const progressPercentage =
            // @ts-expect-error slide has progess as an extra property, swiper slide
            Math.min(Math.abs(slide.progress), swiper.params.threshold || 0) / (swiper.params.threshold || 0)
        const slideOpacity = 1 - easing(progressPercentage)
        const redShift = Math.max(slideOpacity, 0.5)

        slide.style.opacity = String(slideOpacity)
        slideImage.style.opacity = String(redShift)
        slideImage.style.filter = `sepia(${progressPercentage / 2})`
    }
}

function updateBannerHeading(activeSlide: HTMLElement) {
    const name = activeSlide.getAttribute("data-name")
    let location = activeSlide.getAttribute("data-ort") || ""
    if (location) {
        location = `- ${location}`
    }

    const isStar = activeSlide.getAttribute("data-is-star") === "true"
    const heading = document.querySelector(".xxl-header")?.firstElementChild
    if (heading) {
        heading.innerHTML = ""
        const span = document.createElement("span")
        span.innerText = `${name} ${location}`
        if (isStar) {
            const starsIcon = document.createElement("img")
            starsIcon.alt = "LadiesSTARS"
            starsIcon.classList.add("starsContentIcon")
            starsIcon.src = "https://static.ladies-cdn.de/shared/ladies-stars/img/ladies-stars-logo-icon-white.svg"
            heading.append(starsIcon)
        }
        heading.append(span)
    }
}
